import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  Check,
  Clock,
  FileSignature,
  Globe2,
  Image as ImageIcon,
  Loader2,
  Mail,
  MapPin,
  PenLine,
  Plus,
  Save,
  Settings2,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { z } from "zod";
import {
  CONTRACT_TYPES,
  companiesQueries,
  isManager,
  representativesApi,
  wageConfigApi,
  type CompanyContractTypeConfigDto,
  type CompanyDto,
  type ContractTypeKey,
  type LegalRepresentativeDto,
  type OrdioEmploymentTypeDto,
  type OrdioWageTemplateDto,
} from "@/lib/companies/api";
import { useToastMutation } from "@/lib/mutations";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/lib/utils";
import { formatRelative } from "@/lib/format";

type Section = "uebersicht" | "vertragsunterzeichner" | "vertragseinstellungen";

const searchSchema = z.object({
  companyId: z.string().optional(),
  section: z
    .enum(["uebersicht", "vertragsunterzeichner", "vertragseinstellungen"])
    .optional(),
});

export const Route = createFileRoute("/o/$slug/companies")({
  validateSearch: searchSchema,
  component: CompaniesPage,
});

function CompaniesPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const companies = useQuery(companiesQueries.all());
  // Mirror Drive: prefer the URL param, else the first available company.
  const activeCompanyId = search.companyId ?? companies.data?.[0]?.id;
  const activeSection: Section = search.section ?? "uebersicht";

  useEffect(() => {
    if (!search.companyId && activeCompanyId) {
      void navigate({
        search: (prev) => ({ ...prev, companyId: activeCompanyId }),
        replace: true,
      });
    }
  }, [search.companyId, activeCompanyId, navigate]);

  const activeCompany = companies.data?.find((c) => c.id === activeCompanyId);

  return (
    <PageContainer>
      <PageHeader
        title="Unternehmen"
        description="Stammdaten der mit dieser Organisation verknüpften Betriebe (aus Ordio)."
      />

      <CompanyTabs
        companies={companies.data ?? []}
        loading={companies.isLoading}
        error={companies.error}
        activeId={activeCompanyId}
        onChange={(id) =>
          void navigate({
            search: (prev) => ({ ...prev, companyId: id }),
            replace: false,
          })
        }
      />

      {companies.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {companies.error instanceof ApiError && companies.error.status === 403
                ? "Keine Berechtigung."
                : `Konnte Unternehmen nicht laden: ${(companies.error as Error).message}`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void companies.refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {companies.data && companies.data.length === 0 && (
        <div className="rounded-md border bg-card p-12 text-center">
          <Briefcase className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Keine Unternehmen sichtbar. Ein Org-Admin muss zunächst einen Ordio-API-Key in den
            Org-Einstellungen hinterlegen.
          </p>
        </div>
      )}

      {activeCompany && (
        <>
          <SectionTabs
            active={activeSection}
            onChange={(section) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  section: section === "uebersicht" ? undefined : section,
                }),
                replace: true,
              })
            }
          />

          {activeSection === "uebersicht" && (
            <CompanyOverview company={activeCompany} slug={slug} />
          )}
          {activeSection === "vertragsunterzeichner" && (
            <RepresentativesPanel company={activeCompany} />
          )}
          {activeSection === "vertragseinstellungen" && (
            <ContractSettingsPanel company={activeCompany} />
          )}
        </>
      )}
    </PageContainer>
  );
}

// ── Company tab strip (Drive-style) ─────────────────────────────────────────

function CompanyTabs({
  companies,
  loading,
  error,
  activeId,
  onChange,
}: {
  companies: CompanyDto[];
  loading: boolean;
  error: Error | null;
  activeId: string | undefined;
  onChange: (companyId: string) => void;
}) {
  if (loading) {
    return (
      <div className="mb-4 flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-32" />
        ))}
      </div>
    );
  }
  if (error) return null;
  if (companies.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b">
      {companies.map((c) => {
        const active = c.id === activeId;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            className={cn(
              "relative -mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              active
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {c.name}
            {!isManager(c.role) && (
              <span className="ml-2 text-[10px] uppercase text-muted-foreground">Viewer</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Section tabs (Übersicht / Vertragsunterzeichner) ────────────────────────

function SectionTabs({
  active,
  onChange,
}: {
  active: Section;
  onChange: (s: Section) => void;
}) {
  const items: Array<{ key: Section; label: string; icon: React.ReactNode }> = [
    { key: "uebersicht", label: "Übersicht", icon: <Building2 className="h-4 w-4" /> },
    {
      key: "vertragsunterzeichner",
      label: "Vertragsunterzeichner",
      icon: <FileSignature className="h-4 w-4" />,
    },
    {
      key: "vertragseinstellungen",
      label: "Vertragseinstellungen",
      icon: <Settings2 className="h-4 w-4" />,
    },
  ];
  return (
    <div className="mb-4 flex flex-wrap items-center gap-1 rounded-md border bg-card p-1">
      {items.map((it) => {
        const isActive = it.key === active;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className={cn(
              "inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {it.icon}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Übersicht — single company ──────────────────────────────────────────────

function CompanyOverview({ company, slug }: { company: CompanyDto; slug: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h2 className="truncate text-base font-semibold">{company.name}</h2>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">Ordio-ID {company.ordioId}</p>
          </div>
          <RoleBadge role={company.role} />
        </header>

        <Separator className="my-4" />

        <dl className="space-y-2 text-sm">
          <Row icon={<MapPin className="h-3.5 w-3.5" />} label="Adresse">
            <Address company={company} />
          </Row>
          <Row icon={<Globe2 className="h-3.5 w-3.5" />} label="Region">
            <span>
              {company.timezone ?? "—"}
              {company.locale && ` · ${company.locale}`}
              {company.currency && ` · ${company.currency}`}
            </span>
          </Row>
          {company.invoiceEmail && (
            <Row icon={<Mail className="h-3.5 w-3.5" />} label="Rechnungs-E-Mail">
              <a
                href={`mailto:${company.invoiceEmail}`}
                className="truncate text-primary hover:underline"
              >
                {company.invoiceEmail}
              </a>
            </Row>
          )}
        </dl>

        <Separator className="my-4" />

        <footer className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Sync {formatRelative(company.lastSyncedAt) ?? "—"}
          </span>
          <Link
            to="/o/$slug/employees"
            params={{ slug }}
            search={{ companyId: company.id }}
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <Users className="h-3 w-3" />
            Personal anzeigen
          </Link>
        </footer>
      </CardContent>
    </Card>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[6rem_1fr] items-start gap-2">
      <dt className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </dt>
      <dd className="min-w-0 truncate text-sm">{children}</dd>
    </div>
  );
}

function Address({ company }: { company: CompanyDto }) {
  const lines = [
    company.address,
    [company.postcode, company.city].filter(Boolean).join(" "),
    company.country,
  ].filter(Boolean);
  if (lines.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="block leading-tight">
      {lines.map((l, i) => (
        <span key={i} className="block truncate">
          {l}
        </span>
      ))}
    </span>
  );
}

function RoleBadge({ role }: { role: CompanyDto["role"] }) {
  const manager = isManager(role);
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide",
        manager ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
      )}
    >
      {manager ? "Manager" : "Viewer"}
    </span>
  );
}

// ── Vertragsunterzeichner panel ─────────────────────────────────────────────

function RepresentativesPanel({ company }: { company: CompanyDto }) {
  const canManage = isManager(company.role);
  const reps = useQuery(companiesQueries.representatives(company.id));
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Personen mit Unterschriftsberechtigung für Arbeitsverträge dieses Unternehmens.
        </p>
        {canManage && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Hinzufügen
          </Button>
        )}
      </div>

      {reps.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      )}

      {reps.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {reps.error instanceof ApiError && reps.error.status === 403
                ? "Keine Berechtigung."
                : `Konnte Vertragsunterzeichner nicht laden: ${(reps.error as Error).message}`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void reps.refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {reps.data && reps.data.length === 0 && (
        <div className="rounded-md border bg-card p-12 text-center">
          <FileSignature className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Noch keine Vertragsunterzeichner hinterlegt.
          </p>
          {canManage && (
            <Button className="mt-4" size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              Ersten Unterzeichner anlegen
            </Button>
          )}
        </div>
      )}

      {reps.data && reps.data.length > 0 && (
        <ul className="divide-y rounded-md border bg-card">
          {reps.data.map((r) => (
            <li key={r.id}>
              <RepresentativeRow
                companyId={company.id}
                rep={r}
                canManage={canManage}
              />
            </li>
          ))}
        </ul>
      )}

      {!canManage && (
        <p className="text-xs text-muted-foreground">
          Nur Manager können Vertragsunterzeichner verwalten.
        </p>
      )}

      <AddRepresentativeDialog
        companyId={company.id}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </div>
  );
}

function RepresentativeRow({
  companyId,
  rep,
  canManage,
}: {
  companyId: string;
  rep: LegalRepresentativeDto;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [signatureOpen, setSignatureOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: companiesQueries.representatives(companyId).queryKey,
    });

  const remove = useToastMutation({
    mutationFn: () => representativesApi.delete(companyId, rep.id),
    toasts: { success: "Unterzeichner entfernt." },
    onSuccess: () => invalidate(),
  });

  const removeSignature = useToastMutation({
    mutationFn: () => representativesApi.removeSignature(companyId, rep.id),
    toasts: { success: "Unterschrift entfernt." },
    onSuccess: () => invalidate(),
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <PenLine className="h-4 w-4 text-muted-foreground" />
          <span className="truncate font-medium">
            {rep.salutation ? `${rep.salutation} ` : ""}
            {rep.name}
          </span>
          {rep.hasSignature ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-700">
              <Check className="h-3 w-3" />
              Unterschrift
            </span>
          ) : (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Ohne Unterschrift
            </span>
          )}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSignatureOpen(true)}
          >
            <Upload className="h-4 w-4" />
            {rep.hasSignature ? "Unterschrift ersetzen" : "Unterschrift hochladen"}
          </Button>
          {rep.hasSignature && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (window.confirm("Unterschrift wirklich entfernen?")) {
                  removeSignature.mutate(undefined);
                }
              }}
              disabled={removeSignature.isPending}
            >
              {removeSignature.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Entfernen
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (
                window.confirm(
                  `"${rep.name}" wirklich als Vertragsunterzeichner entfernen?`,
                )
              ) {
                remove.mutate(undefined);
              }
            }}
            disabled={remove.isPending}
          >
            {remove.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Löschen
          </Button>
        </div>
      )}

      <SignatureUploadDialog
        companyId={companyId}
        representativeId={rep.id}
        representativeName={rep.name}
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
      />
    </div>
  );
}

// ── Add dialog ──────────────────────────────────────────────────────────────

function AddRepresentativeDialog({
  companyId,
  open,
  onOpenChange,
}: {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [salutation, setSalutation] = useState("");
  const [name, setName] = useState("");

  const reset = () => {
    setSalutation("");
    setName("");
  };

  const create = useToastMutation({
    mutationFn: () =>
      representativesApi.create(companyId, {
        salutation: salutation.trim() || null,
        name: name.trim(),
      }),
    toasts: { success: "Unterzeichner angelegt." },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companiesQueries.representatives(companyId).queryKey,
      });
      reset();
      onOpenChange(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vertragsunterzeichner anlegen</DialogTitle>
          <DialogDescription>
            Diese Person erscheint im Vertragsassistenten zur Auswahl als Unterzeichner.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="rep-salutation">Anrede (optional)</Label>
            <Input
              id="rep-salutation"
              value={salutation}
              onChange={(e) => setSalutation(e.target.value)}
              placeholder="z. B. Herr, Frau, Dr."
              maxLength={64}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rep-name">Name</Label>
            <Input
              id="rep-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vor- und Nachname"
              maxLength={256}
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              Abbrechen
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={() => create.mutate(undefined)}
            disabled={!name.trim() || create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Signature upload dialog ─────────────────────────────────────────────────

function SignatureUploadDialog({
  companyId,
  representativeId,
  representativeName,
  open,
  onOpenChange,
}: {
  companyId: string;
  representativeId: string;
  representativeName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [pin, setPin] = useState("");

  const reset = () => {
    setFile(null);
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return null;
    });
    setPin("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const upload = useToastMutation({
    mutationFn: () => {
      if (!file) throw new Error("Keine Datei ausgewählt.");
      if (!pin.trim()) throw new Error("PIN ist erforderlich.");
      return representativesApi.uploadSignature(
        companyId,
        representativeId,
        file,
        pin.trim(),
      );
    },
    toasts: { success: "Unterschrift hochgeladen." },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companiesQueries.representatives(companyId).queryKey,
      });
      reset();
      onOpenChange(false);
    },
  });

  const onPickFile = (f: File | null) => {
    setFile(f);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unterschrift hochladen</DialogTitle>
          <DialogDescription>
            Für „{representativeName}". PNG, JPEG oder WebP, max. 2 MB. Die PIN wird zur
            späteren Bestätigung der Verwendung benötigt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sig-file">Datei</Label>
            <input
              ref={fileInputRef}
              id="sig-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
            />
          </div>

          {preview && (
            <div className="rounded-md border bg-muted/40 p-3">
              <div className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
                <ImageIcon className="h-3 w-3" />
                Vorschau
              </div>
              <img
                src={preview}
                alt="Signatur-Vorschau"
                className="mx-auto max-h-40 max-w-full object-contain"
              />
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="sig-pin">PIN</Label>
            <Input
              id="sig-pin"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="PIN für diese Unterschrift"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              Abbrechen
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={() => upload.mutate(undefined)}
            disabled={!file || !pin.trim() || upload.isPending}
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Hochladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Vertragseinstellungen panel ─────────────────────────────────────────────
//
// Per contract type (Minijob / Vollzeit / Teilzeit / Werkstudent), pick an
// Ordio wage template + Ordio employment type. Saving PUTs each contract type
// independently — partial saves are fine. Both fields are required by the
// PUT contract; the panel disables Save until both are picked.

function ContractSettingsPanel({ company }: { company: CompanyDto }) {
  const canManage = isManager(company.role);
  const config = useQuery(companiesQueries.wageConfig(company.id));
  const templates = useQuery(companiesQueries.ordioWageTemplates(company.id));
  const employmentTypes = useQuery(
    companiesQueries.ordioEmploymentTypes(company.id),
  );

  const loading = config.isLoading || templates.isLoading || employmentTypes.isLoading;
  const error = config.error ?? templates.error ?? employmentTypes.error;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Welche Ordio-Lohnvorlage und Anstellungsart pro Vertragstyp im Vertragsassistenten
        vorbelegt werden.
      </p>

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {error instanceof ApiError && error.status === 403
                ? "Keine Berechtigung."
                : `Konnte Vertragseinstellungen nicht laden: ${(error as Error).message}`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              void config.refetch();
              void templates.refetch();
              void employmentTypes.refetch();
            }}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {!loading && !error && config.data && templates.data && employmentTypes.data && (
        <div className="space-y-3">
          {CONTRACT_TYPES.map((type) => {
            const entry =
              config.data!.find((c) => c.contractType === type) ??
              ({
                contractType: type,
                ordioWageTemplateId: null,
                ordioEmploymentId: null,
              } satisfies CompanyContractTypeConfigDto);
            return (
              <ContractTypeRow
                key={type}
                companyId={company.id}
                type={type}
                entry={entry}
                templates={templates.data!}
                employmentTypes={employmentTypes.data!}
                canManage={canManage}
              />
            );
          })}
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-muted-foreground">
          Nur Manager können Vertragseinstellungen ändern.
        </p>
      )}
    </div>
  );
}

function ContractTypeRow({
  companyId,
  type,
  entry,
  templates,
  employmentTypes,
  canManage,
}: {
  companyId: string;
  type: ContractTypeKey;
  entry: CompanyContractTypeConfigDto;
  templates: OrdioWageTemplateDto[];
  employmentTypes: OrdioEmploymentTypeDto[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<string>(
    entry.ordioWageTemplateId ?? "",
  );
  const [employmentId, setEmploymentId] = useState<string>(
    entry.ordioEmploymentId != null ? String(entry.ordioEmploymentId) : "",
  );

  // Reset local state when the server's snapshot for this row changes (e.g.
  // after refetch following a save on a sibling row).
  useEffect(() => {
    setTemplateId(entry.ordioWageTemplateId ?? "");
    setEmploymentId(entry.ordioEmploymentId != null ? String(entry.ordioEmploymentId) : "");
  }, [entry.ordioWageTemplateId, entry.ordioEmploymentId]);

  const dirty =
    templateId !== (entry.ordioWageTemplateId ?? "") ||
    employmentId !==
      (entry.ordioEmploymentId != null ? String(entry.ordioEmploymentId) : "");

  const canSave = canManage && dirty && !!templateId && !!employmentId;

  const save = useToastMutation({
    mutationFn: () =>
      wageConfigApi.set(companyId, type, {
        ordioWageTemplateId: templateId,
        ordioEmploymentId: parseInt(employmentId, 10),
      }),
    toasts: { success: "Gespeichert." },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companiesQueries.wageConfig(companyId).queryKey,
      });
    },
  });

  const isConfigured =
    entry.ordioWageTemplateId != null && entry.ordioEmploymentId != null;

  return (
    <Card>
      <CardContent className="p-4">
        <header className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{type}</h3>
          </div>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide",
              isConfigured
                ? "bg-emerald-100 text-emerald-700"
                : "bg-muted text-muted-foreground",
            )}
          >
            {isConfigured ? "Konfiguriert" : "Nicht konfiguriert"}
          </span>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`tmpl-${type}`}>Ordio-Lohnvorlage</Label>
            <select
              id={`tmpl-${type}`}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={!canManage}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">— auswählen —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({formatTemplateRate(t)})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`emp-${type}`}>Anstellungsart</Label>
            <select
              id={`emp-${type}`}
              value={employmentId}
              onChange={(e) => setEmploymentId(e.target.value)}
              disabled={!canManage}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">— auswählen —</option>
              {employmentTypes.map((e) => (
                <option key={e.id} value={String(e.id)}>
                  {e.name}
                  {e.type ? ` (${e.type})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {canManage && (
          <div className="mt-3 flex items-center justify-end gap-2">
            {dirty && (
              <span className="text-xs text-amber-600">Ungespeicherte Änderungen</span>
            )}
            <Button
              size="sm"
              onClick={() => save.mutate(undefined)}
              disabled={!canSave || save.isPending}
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Speichern
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatTemplateRate(t: OrdioWageTemplateDto): string {
  const n = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const suffix =
    t.defaultWageType === "monthly"
      ? "€/Monat"
      : t.defaultWageType === "daily"
        ? "€/Tag"
        : "€/Std";
  return `${n.format(t.defaultWage)} ${suffix}`;
}
